import { create } from 'zustand';
import { authFetch } from '../lib/authFetch';
import { useProjectStore, readJsonOrThrow } from './projectStore';
import { useToleranceStore } from './toleranceStore';
import { copyName } from '../utils/versionText';
import { trackEvent } from '../lib/analytics';

// Versionen der aktuell geöffneten Cloud-Berechnung (Spec 2026-10-20):
// Liste, benennen, löschen, Vorschau, wiederherstellen, kopieren.

export function versionsUrl(projectId, calcId, suffix = '') {
  return `/api/projects/${projectId}/calculations/${calcId}/versions${suffix}`;
}

function current() {
  const { currentProject, currentCalculation } = useProjectStore.getState();
  if (!currentProject || !currentCalculation) {
    throw new Error('Keine gespeicherte Berechnung geöffnet.');
  }
  return { projectId: currentProject.id, calc: currentCalculation };
}

// Offene Änderungen zuerst speichern, damit ein Stand bzw. die Vorschau
// nie unsichtbar verwirft, woran gerade gearbeitet wurde. Wirft weiter.
async function saveIfDirty() {
  const t = useToleranceStore.getState();
  if (!t.preview && t.isDirty) await useProjectStore.getState().saveCurrentCalculation();
}

export const useVersionStore = create((set, get) => ({
  versions: [],
  loading: false,
  error: null,
  panelOpen: false,
  nameDialogOpen: false,
  busy: false,

  openPanel: () => { set({ panelOpen: true }); get().fetchVersions(); },
  closePanel: () => set({ panelOpen: false }),
  openNameDialog: () => set({ nameDialogOpen: true }),
  closeNameDialog: () => set({ nameDialogOpen: false }),

  fetchVersions: async () => {
    set({ loading: true, error: null });
    try {
      const { projectId, calc } = current();
      const res = await authFetch(versionsUrl(projectId, calc.id));
      set({ versions: await readJsonOrThrow(res, 'Versionen laden') });
    } catch (err) {
      set({ error: err.message });
    } finally {
      set({ loading: false });
    }
  },

  nameVersion: async (label) => {
    await saveIfDirty();
    const { projectId, calc } = current();
    const res = await authFetch(versionsUrl(projectId, calc.id), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label })
    });
    const created = await readJsonOrThrow(res, 'Stand benennen');
    set(state => ({ versions: [created, ...state.versions] }));
    trackEvent('Version Named');
    return created;
  },

  deleteVersion: async (id) => {
    const { projectId, calc } = current();
    const res = await authFetch(versionsUrl(projectId, calc.id, `/${id}`), { method: 'DELETE' });
    await readJsonOrThrow(res, 'Stand löschen');
    set(state => ({ versions: state.versions.filter(v => v.id !== id) }));
  },

  startPreview: async (entry) => {
    await saveIfDirty();
    const { projectId, calc } = current();
    set({ busy: true });
    try {
      const res = await authFetch(versionsUrl(projectId, calc.id, `/${entry.id}`));
      const v = await readJsonOrThrow(res, 'Stand laden');
      useToleranceStore.getState().loadProject(v.data || {}, v.name, calc.id, calc.calculation_index, {
        proMode: false,
        preview: { id: v.id, kind: v.kind, label: v.label ?? null, created_at: v.created_at }
      });
    } finally {
      set({ busy: false });
    }
  },

  endPreview: async () => {
    const { calc } = current();
    await useProjectStore.getState().loadCalculation(calc);
  },

  restorePreview: async () => {
    const preview = useToleranceStore.getState().preview;
    if (!preview) return null;
    const { projectId, calc } = current();
    set({ busy: true });
    try {
      const res = await authFetch(versionsUrl(projectId, calc.id, `/${preview.id}/restore`), { method: 'POST' });
      const row = await readJsonOrThrow(res, 'Wiederherstellen');
      useProjectStore.setState(state => ({
        calculations: state.calculations.map(c => c.id === row.id ? row : c)
      }));
      await useProjectStore.getState().loadCalculation(row);
      trackEvent('Version Restored');
      if (get().panelOpen) get().fetchVersions();
      return row;
    } finally {
      set({ busy: false });
    }
  },

  copyPreview: async () => {
    const t = useToleranceStore.getState();
    const preview = t.preview;
    if (!preview) return null;
    const { projectId, calc } = current();
    set({ busy: true });
    try {
      // Daten aus der Version selbst, nicht aus dem Editor (der könnte per
      // Methodenwechsel abweichen).
      const res = await authFetch(versionsUrl(projectId, calc.id, `/${preview.id}`));
      const v = await readJsonOrThrow(res, 'Stand laden');
      return await useProjectStore.getState().createCalculation({
        name: copyName(calc.name, preview),
        data: v.data
      });
    } finally {
      set({ busy: false });
    }
  }
}));
